// Renders one visible map layer as Leaflet markers. Each function maps a
// layer's items (from mapLayerModel.layerItems) to <Marker> elements, choosing
// the icon per type. Grouping, value aggregation and donut math live in
// mapLayerModel so the PNG exporter paints the same things.
import React from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { divIcon as leafletDivIcon } from 'leaflet';
import {
  PALETTES,
  dotIconHtml,
  pieIconHtml,
  barIconHtml,
  donutIconHtml,
  labelIconHtml
} from './mapIcons';
import { valueFor, fractionsOf, resolveDonutColumns, itemDonutParts } from './mapLayerModel';
import { themeColor } from '../theme';

export const MARKER_TRACK = 'rgba(0,0,0,0.15)';

export function renderDots(layer, items) {
  const { color, size } = layer.options;
  const dim = Math.max(2, (Number(size) || 4) * 2);
  return items.map((it, i) => (
    <Marker key={`${layer.id}-d${i}`} position={[it.lat, it.lon]} icon={dotIcon(color, size, dim)}>
      <Tooltip>{it.label}</Tooltip>
    </Marker>
  ));
}

function dotIcon(color, size, dim) {
  return leafletDivIcon({
    html: dotIconHtml(size, color),
    className: 'viz-marker-icon',
    iconSize: [dim, dim],
    iconAnchor: [dim / 2, dim / 2]
  });
}

export function renderPie(layer, items) {
  const palette = PALETTES[layer.options.palette] || PALETTES.default;
  const scale = Number(layer.options.size) || 1;
  const values = items.map((it) => valueFor(it, layer));
  const fractions = fractionsOf(values);
  return items.map((it, i) => {
    const icon = leafletDivIcon(
      iconConfig(
        pieIconHtml(fractions[i], palette[i % palette.length], MARKER_TRACK, scale),
        40,
        40,
        [20, 20]
      )
    );
    return (
      <Marker key={`${layer.id}-p${i}`} position={[it.lat, it.lon]} icon={icon}>
        <Tooltip>
          {it.label}: {values[i]}
        </Tooltip>
      </Marker>
    );
  });
}

export function renderBar(layer, items) {
  const { color, size } = layer.options;
  const scale = Number(size) || 1;
  const values = items.map((it) => valueFor(it, layer));
  const fractions = fractionsOf(values);
  return items.map((it, i) => {
    const icon = leafletDivIcon(
      iconConfig(barIconHtml(fractions[i], color, scale), 16, 36, [8, 36])
    );
    return (
      <Marker key={`${layer.id}-b${i}`} position={[it.lat, it.lon]} icon={icon}>
        <Tooltip>
          {it.label}: {values[i]}
        </Tooltip>
      </Marker>
    );
  });
}

export function renderDonuts(layer, items) {
  const palette = PALETTES[layer.options.palette] || PALETTES.default;
  const scale = Number(layer.options.size) || 1;
  const columns = resolveDonutColumns(layer, items);

  return items.map((it, i) => {
    const parts = itemDonutParts(it, columns, layer.options.labels);
    const total = parts.reduce((s, p) => s + (p.value || 0), 0);
    const icon = leafletDivIcon(iconConfig(donutIconHtml(parts, palette, scale), 40, 40, [20, 20]));
    return (
      <Marker key={`${layer.id}-u${i}`} position={[it.lat, it.lon]} icon={icon}>
        <Tooltip>
          {it.label}
          {parts.map((part) => {
            const pct = total > 0 ? ((part.value / total) * 100).toFixed(0) : '—';
            return (
              <div key={part.label}>
                {part.label}: {part.value == null ? '—' : part.value} ({pct}%)
              </div>
            );
          })}
        </Tooltip>
      </Marker>
    );
  });
}

export function renderLabels(layer, items) {
  const field = layer.options.field;
  const color = themeColor('--md-sys-color-on-surface', '#1c1b1f');
  return items.map((it, i) => {
    const raw = field != null && it.members[0] ? it.members[0][field] : null;
    const text = raw == null ? it.label : String(raw);
    const icon = leafletDivIcon(
      iconConfig(labelIconHtml(text, color), 1, 1, [0, 0], 1, 'viz-marker-label')
    );
    return (
      <Marker
        key={`${layer.id}-l${i}`}
        position={[it.lat, it.lon]}
        icon={icon}
        interactive={false}
      />
    );
  });
}

// Build a plain Leaflet divIcon *config* object (not yet an Icon instance).
// `width`/`height` are the icon's natural size in px (icons are not assumed
// square); `anchor` is [x, y] in natural px (default center-x, bottom);
// `scale` multiplies all of them. Callers wrap the result in L.divIcon so the
// Marker receives a real icon with a createIcon() method.
function iconConfig(html, width, height, anchor, scale, className = 'viz-marker-icon') {
  const s = Math.max(1, Number(scale) || 1);
  const ax = (anchor && anchor[0]) || width / 2;
  const ay = (anchor && anchor[1]) || height;
  return {
    html,
    className,
    iconSize: [Math.round(width * s), Math.round(height * s)],
    iconAnchor: [Math.round(ax * s), Math.round(ay * s)]
  };
}
